import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { Address, createAddress, IFormModel } from '@mwe/models';
import { FormComponent } from '../form/form.component';
import { SelectFirstInvalidInputDirective } from '../form/inputs/select-first-invalid-input/select-first-invalid-input.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';

const POST_CODE_MIN_LENGTH = 1;
const POST_CODE_MAX_LENGTH = 10;

@Component({
  selector: 'mwe-manual-address-input',
  templateUrl: './manual-address-input.component.html',
  imports: [FormComponent, SelectFirstInvalidInputDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManualAddressInputComponent implements OnInit, AfterViewInit {
  initialData = input<Address>();

  readonly formChanged = output<boolean>();

  formRef = viewChild(FormComponent);

  formAddress = signal<Address>(null);

  formModel: IFormModel;
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.createFormModel();
  }

  ngAfterViewInit() {
    this.formRef()
      ?.form?.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.formAddress.set(this.getAddressFromForm())),
        tap(() => this.formChanged.emit(true)),
      )
      .subscribe();
  }

  getAddressFromForm(): Address {
    const address = createAddress({
      id: null,
      street: this.formRef()?.form.value['manual-address-street'],
      streetNumber: this.formRef()?.form.value['manual-address-streetNumber'],
      block: this.formRef()?.form.value['manual-address-blockNumber'],
      doorNumber: this.formRef()?.form.value['manual-address-doorNumber'],
      hint: null, // there is no adresshinweis
      postcode: this.formRef()?.form.value['manual-address-post-code'],
      city: this.formRef()?.form.value['manual-address-city'],
      country: null,
    });

    this.formAddress.set(address);

    return address;
  }

  validate(): boolean {
    this.formRef()?.fireValidation();

    return this.formRef()?.form.valid;
  }

  private createFormModel(): void {
    this.formModel = {
      updateOn: 'blur',
      inputs: [
        {
          name: 'row-1',
          inputs: [
            {
              name: 'manual-address-post-code',
              initialValue: this.initialData()?.postcode,
              validators: [Validators.required, Validators.minLength(POST_CODE_MIN_LENGTH), Validators.maxLength(POST_CODE_MAX_LENGTH)],
              labelKey: 'shared.international-address-input.form.postCode.title',
              validationErrorLabelKey: 'shared.international-address-input.form.postCode.error',
              validate: true,
              dataType: 'text',
              directiveType: 'integer',
              maxLength: POST_CODE_MAX_LENGTH,
              updateOn: 'change',
              inputClasses: 'col-12 col-md-4',
            },
            {
              name: 'manual-address-city',
              initialValue: this.initialData()?.city,
              validators: [Validators.required, Validators.maxLength(30)],
              labelKey: 'shared.international-address-input.form.city.title',
              validationErrorLabelKey: 'shared.international-address-input.form.city.error',
              validate: true,
              dataType: 'text',
              maxLength: 30,
              inputClasses: 'col-12 col-md-8',
            },
          ],
        },
        {
          name: 'row-2',
          inputs: [
            {
              name: 'manual-address-street',
              initialValue: this.initialData()?.street,
              validators: [Validators.required, Validators.maxLength(30)],
              labelKey: 'shared.international-address-input.form.street.title',
              validationErrorLabelKey: 'shared.international-address-input.form.street.error',
              validate: true,
              dataType: 'text',
              maxLength: 30,
              inputClasses: 'col-12 col-md-8',
            },
            {
              name: 'manual-address-streetNumber',
              initialValue: this.initialData()?.streetNumber,
              validators: [Validators.required, Validators.maxLength(10)],
              labelKey: 'shared.international-address-input.form.streetNumber.title',
              validationErrorLabelKey: 'shared.international-address-input.form.streetNumber.error',
              validate: true,
              dataType: 'text',
              maxLength: 10,
              inputClasses: 'col-12 col-md-4',
            },
          ],
        },
        {
          name: 'row-3',
          inputs: [
            {
              name: 'manual-address-blockNumber',
              initialValue: this.initialData()?.block,
              validators: [Validators.maxLength(10)],
              labelKey: 'shared.international-address-input.form.blockNumber.title',
              placeholder: 'shared.international-address-input.form.blockNumber.placeholder',
              validationErrorLabelKey: 'shared.international-address-input.form.blockNumber.error',
              validate: true,
              dataType: 'text',
              maxLength: 10,
              inputClasses: 'col-12 col-md-6',
            },
            {
              name: 'manual-address-doorNumber',
              initialValue: this.initialData()?.doorNumber,
              validators: [Validators.maxLength(10)],
              labelKey: 'shared.international-address-input.form.doorNumber.title',
              placeholder: 'shared.international-address-input.form.doorNumber.placeholder',
              validationErrorLabelKey: 'shared.international-address-input.form.doorNumber.error',
              validate: true,
              dataType: 'text',
              maxLength: 10,
              inputClasses: 'col-12 col-md-6',
            },
          ],
        },
      ],
    };
  }
}
